import { useCallback } from "react";
import { Link } from "@chakra-ui/react";
import { FormattedMessage } from "react-intl";
import { useSettings } from "utils/settingsContext";
import { useFlowConfig } from "flows/hooks";
import { isRedirectURLAllowed } from "utils/redirect";
import { useSessionCtx, inIframe, usePostMessage } from "utils";

/**
 * ExternalEmailModLink
 * @name ExternalEmailModLink
 * @param {object} [primaryEmailPayload] - this is optional only in the sense that the component will show nothing unless we have the payload and the boolean value below
 * @param {boolean} [primaryEmailPayload.is_verified] - see note above, we show nothing if this value exists and it is true
 * @returns JSX.Element
 * @description This is a funky little component that currently only exists for SL.  It will
 * show a link which when clicked will take the user to a location (external) where they can
 * modify the primary user email we have for them in our system.  This functionality expects that external
 * link (run by integrator) to modify and then update our record for that account id.
 * To determine if we show this, a number of items need to be true:
 *     - the integrator has a acweb.feature_flags setting 'disablePrimaryEmailUpdateByUser' set to true
 *     - the flowConfig contains a 'externalEmailModificationLink' value
 *     - the primary email we currently have for the user is NOT verified
 *     - externalEmailModificationLink value is valid against the integrator.acweb.allowed_domain_redirect_patterns list
 */
const ExternalEmailModLink = ({ primaryEmailPayload }) => {
  const { integratorSettings } = useSettings();
  const { sessionCtx } = useSessionCtx();
  const { externalEmailModificationLink } = useFlowConfig();
  const { postMessage } = usePostMessage(sessionCtx?.flow, sessionCtx?.intCtx);

  const disablePrimaryEmailUpdateByUser =
    integratorSettings?.feature_flags?.disablePrimaryEmailUpdateByUser === true;

  const isURLAllowed =
    externalEmailModificationLink &&
    !!integratorSettings.allowed_domain_redirect_patterns &&
    isRedirectURLAllowed(externalEmailModificationLink, integratorSettings); // isRedirectURLAllowed currently allows any url if allowed_domain_redirect_patterns is not defined for the integrator.  Gonna be more strict here and only allow if it is defined AND allowed_domain_redirect_patterns is defined
  const emailIsUnVerified = primaryEmailPayload?.is_verified === false; // make sure we have a primary email payload and the email is not verified.  Verified emails do not need the link out (currently)
  const showExtEmailModLink =
    disablePrimaryEmailUpdateByUser &&
    externalEmailModificationLink &&
    emailIsUnVerified &&
    isURLAllowed; // this is narrowly focused to the publisher having the setting disablePrimaryEmailUpdateByUser == true (currenlyt only SL), and them supplying a link in the flowConfig

  const handleLinkClick = useCallback(() => {
    if (inIframe()) {
      postMessage(`tilia.${sessionCtx?.flow}.redirect`, {
        redirect: externalEmailModificationLink,
      });
    } else {
      // handle outside of iFrame
      window.location.href = externalEmailModificationLink;
    }
  }, [externalEmailModificationLink, postMessage, sessionCtx]);

  if (showExtEmailModLink) {
    return (
      <Link as="i" onClick={handleLinkClick} fontSize="xs">
        <FormattedMessage
          defaultMessage="To modify your email, please click here"
          description="link"
        />
      </Link>
    );
  }
  return null;
};

export default ExternalEmailModLink;
